import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Hook pour gérer le heartbeat et récupérer les statuts en ligne
export const useOnlineStatus = () => {
  const { api, user } = useAuth();
  const [onlineStatus, setOnlineStatus] = useState({});

  // Envoie un heartbeat toutes les 30 secondes
  useEffect(() => {
    if (!user) return;

    const sendHeartbeat = async () => {
      try {
        await api.post('/auth/heartbeat');
      } catch (error) {
        // Silently fail
      }
    };

    // Initial heartbeat
    sendHeartbeat();

    // Periodic heartbeat
    const interval = setInterval(sendHeartbeat, 30000);

    return () => clearInterval(interval);
  }, [api, user]);

  // Récupère les statuts en ligne toutes les 60 secondes
  const fetchOnlineStatus = useCallback(async () => {
    try {
      const res = await api.get('/members/online-status');
      setOnlineStatus(res.data);
    } catch (error) {
      // Silently fail
    }
  }, [api]);

  useEffect(() => {
    if (!user) return;

    fetchOnlineStatus();
    const interval = setInterval(fetchOnlineStatus, 60000);

    return () => clearInterval(interval);
  }, [user, fetchOnlineStatus]);

  return { onlineStatus, refreshOnlineStatus: fetchOnlineStatus };
};

// Composant d'indicateur de statut en ligne
export const OnlineIndicator = ({ userId, onlineStatus, size = 'sm' }) => {
  const isOnline = onlineStatus?.[userId]?.online;
  
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <span 
      className={`inline-block ${sizeClasses[size] || sizeClasses.sm} rounded-full ${
        isOnline 
          ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]' 
          : 'bg-gray-400'
      }`}
      title={isOnline ? 'En ligne' : 'Hors ligne'}
    />
  );
};

// Composant qui affiche le statut textuellement
export const OnlineStatusText = ({ userId, onlineStatus }) => {
  const status = onlineStatus?.[userId];
  
  if (!status) return null;

  if (status.online) {
    return (
      <span className="text-xs text-green-500 flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
        En ligne
      </span>
    );
  }

  if (status.last_seen) {
    const lastSeen = new Date(status.last_seen);
    const now = new Date();
    const diffMinutes = Math.floor((now - lastSeen) / 60000);
    
    let timeText;
    if (diffMinutes < 60) {
      timeText = `il y a ${diffMinutes} min`;
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      timeText = `il y a ${hours}h`;
    } else {
      const days = Math.floor(diffMinutes / 1440);
      timeText = `il y a ${days}j`;
    }
    
    return (
      <span className="text-xs text-muted-foreground flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
        {timeText}
      </span>
    );
  }

  return null;
};

export default { useOnlineStatus, OnlineIndicator, OnlineStatusText };
